import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { lbWorkflowToBase64, reactFlowToLBWorkflow } from './serialization';
import type { ReactFlowEdge, ReactFlowNode } from './types';
import type { WorkflowGraphWriteResult } from '@/apis/logiblocksAPI';

/**
 * Auto-persist lifecycle status for a workflow:
 *   - idle:    no edits since load
 *   - pending: edits made; debounce timer running
 *   - saving:  request in flight
 *   - saved:   last save acked
 *   - error:   last save threw
 */
export type AutoPersistStatus =
  'idle' | 'pending' | 'saving' | 'saved' | 'error';

/** Trailing-debounce window. Picked to absorb panel typing without spamming the backend. */
export const DEFAULT_DEBOUNCE_MS = 750;
/** Upper bound so a long burst of edits still flushes within ~5s. */
export const DEFAULT_MAX_WAIT_MS = 5000;

export interface UseAutoPersistWorkflowOptions {
  /** Identifies the workflow being edited. Changing this resets baseline + cancels pending saves. */
  workflowId: string;
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  /**
   * Persists the workflow to the backend. Must reject on failure so status
   * reflects it. When invoked with `keepalive: true`, the call is fired during
   * page unload — the implementation should use fetch keepalive so the request
   * survives the document's destruction.
   */
  onPersist: (
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[],
    opts?: { keepalive?: boolean }
  ) => Promise<WorkflowGraphWriteResult>;
  /** Whether to auto-persist. Pass false for read-only/published workflows. */
  enabled: boolean;
  /** Pause new debounce scheduling while Copilot owns the graph. */
  paused?: boolean;
  debounceMs?: number;
  maxWaitMs?: number;
}

export interface UseAutoPersistWorkflowResult {
  status: AutoPersistStatus;
  /** Wall-clock time of last successful save, or null if none yet. */
  lastSavedAt: number | null;
  /** Force any pending debounced save to fire and await its completion. */
  flushSave: (
    nodes?: ReactFlowNode[],
    edges?: ReactFlowEdge[]
  ) => Promise<WorkflowGraphWriteResult | null>;
  /** Mark a known server-authoritative snapshot as the local baseline without
   * issuing a write. Used after a failed Copilot save is reconciled to the
   * previously committed graph. */
  adoptAuthoritativeSnapshot: (
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[]
  ) => void;
}

/**
 * Auto-persists a logiblocks workflow to the backend whenever its graph changes.
 *
 * Watches `[nodes, edges]` and schedules a trailing-debounced save. Skips the
 * initial hydration render so a freshly-loaded workflow doesn't immediately
 * round-trip back to the server.
 */
export function useAutoPersistWorkflow({
  workflowId,
  nodes,
  edges,
  onPersist,
  enabled,
  paused = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  maxWaitMs = DEFAULT_MAX_WAIT_MS,
}: UseAutoPersistWorkflowOptions): UseAutoPersistWorkflowResult {
  const [status, setStatus] = useState<AutoPersistStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  // Snapshot of what the next save will write. Captured at schedule time so
  // that re-renders (possibly with a different workflow or onPersist) don't
  // corrupt the payload that was queued for the prior workflow. Single-slot:
  // a new mutation simply overwrites the previous pending snapshot. The
  // workflowId tag lets doSave skip status updates for saves that complete
  // after the user has switched to a different workflow.
  const pendingRef = useRef<{
    workflowId: string;
    nodes: ReactFlowNode[];
    edges: ReactFlowEdge[];
    onPersist: UseAutoPersistWorkflowOptions['onPersist'];
    /** Stable semantic bytes for this exact snapshot. Explicit Copilot
     * flushes use these to advance the baseline only after the PUT acks. */
    bytes?: string;
    completion?: {
      resolve: (result: WorkflowGraphWriteResult) => void;
      reject: (error: unknown) => void;
    };
  } | null>(null);

  // Mirror of the latest workflowId prop. Read inside doSave to detect saves
  // whose status updates would leak into a freshly-loaded workflow's UI.
  const activeWorkflowIdRef = useRef(workflowId);
  activeWorkflowIdRef.current = workflowId;

  // Promise of the currently-running save chain, or null when idle. Used to
  // refuse a second concurrent invocation and to let flushSave await it.
  const inFlightRef = useRef<Promise<void> | null>(null);

  // Latest snapshot the user has produced that we haven't confirmed-saved yet.
  // Set on schedule, cleared after the chain drains successfully. Read by the
  // unload handler to decide whether to fire a keepalive save.
  const unsavedRef = useRef<{
    workflowId: string;
    nodes: ReactFlowNode[];
    edges: ReactFlowEdge[];
    onPersist: UseAutoPersistWorkflowOptions['onPersist'];
  } | null>(null);

  // Per-workflow sentinel so the first [nodes, edges] effect run after a
  // workflow load is skipped (the load itself isn't a user edit).
  const seenInitialRef = useRef<string | null>(null);

  // Bytes of the most recently scheduled graph. Used to short-circuit the
  // schedule effect when a [nodes, edges] change produces no semantic
  // difference (selection toggles, layout recompute, etc.) so the user
  // doesn't see a "Saving…" indicator for non-mutating clicks.
  const lastBytesRef = useRef<string | null>(null);

  // Drain pendingRef in a loop so any mutation that lands while a save is in
  // flight is sent in a follow-up, with "latest wins" semantics — and we never
  // have two PUTs racing the backend at once.
  const doSave = useCallback((): Promise<void> => {
    if (inFlightRef.current) {
      return inFlightRef.current;
    }
    const chain = (async () => {
      let lastIterSucceeded = false;
      try {
        while (pendingRef.current) {
          const pending = pendingRef.current;
          pendingRef.current = null;
          // Only surface status for saves belonging to the currently-active
          // workflow — a save that completes after the user switched away
          // shouldn't paint "Saved" / "Saving" / "Error" onto the new one.
          const isActive = () =>
            pending.workflowId === activeWorkflowIdRef.current;
          if (isActive()) {
            setStatus('saving');
          }
          try {
            const result = await pending.onPersist(
              pending.nodes,
              pending.edges
            );
            // An explicit flush can persist a snapshot while the regular
            // scheduling effect is paused (Copilot owns the canvas). Advance
            // the semantic baseline only after that exact save succeeds, and
            // only when no newer snapshot is waiting behind it. This prevents
            // unpausing the canvas from issuing a redundant same-byte PUT
            // without letting an older completion mask newer work.
            if (
              isActive() &&
              pending.bytes !== undefined &&
              pendingRef.current === null
            ) {
              lastBytesRef.current = pending.bytes;
            }
            pending.completion?.resolve(result);
            if (isActive()) {
              setStatus('saved');
              setLastSavedAt(Date.now());
            }
            lastIterSucceeded = true;
          } catch (err) {
            pending.completion?.reject(err);
            if (isActive()) {
              setStatus('error');
            }
            console.error('Auto-persist failed', err);
            lastIterSucceeded = false;
          }
        }
        // The loop exited because pendingRef is empty. If our final attempt
        // succeeded, the user's edits are durable — clear the unload fallback.
        if (lastIterSucceeded) {
          unsavedRef.current = null;
        }
      } finally {
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = chain;
    return chain;
  }, []);

  // Build the debouncer once per [debounceMs, maxWaitMs] tuple.
  const debouncedSave = useMemo(
    () =>
      debounce(doSave, debounceMs, {
        leading: false,
        trailing: true,
        maxWait: maxWaitMs,
      }),
    [doSave, debounceMs, maxWaitMs]
  );

  // Flush any pending save for the previous workflow, then reset baselines and
  // the visible status — the new workflow starts from a clean "idle" slate.
  useEffect(() => {
    debouncedSave.flush();
    seenInitialRef.current = null;
    lastBytesRef.current = null;
    setStatus('idle');
    setLastSavedAt(null);
  }, [workflowId, debouncedSave]);

  // Schedule a save when the nodes/edges array refs change AND the serialized
  // proto actually differs from the prior schedule. Selection-only changes
  // and layout recompute produce a new array ref but the same proto bytes,
  // so we short-circuit before any status change or PUT.
  useEffect(() => {
    if (!enabled || paused) {
      return;
    }
    const newBytes = lbWorkflowToBase64(reactFlowToLBWorkflow(nodes, edges));
    if (seenInitialRef.current !== workflowId) {
      seenInitialRef.current = workflowId;
      lastBytesRef.current = newBytes;
      return;
    }
    if (newBytes === lastBytesRef.current) {
      return;
    }
    lastBytesRef.current = newBytes;
    const snapshot = { workflowId, nodes, edges, onPersist };
    pendingRef.current = snapshot;
    unsavedRef.current = snapshot;
    setStatus('pending');
    debouncedSave();
  }, [nodes, edges, onPersist, enabled, paused, workflowId, debouncedSave]);

  // Wait for all save activity to settle: kick off any queued save now, then
  // await whatever's running. Used by navigation to avoid losing the last edit.
  const flushSave = useCallback(
    async (
      finalNodes?: ReactFlowNode[],
      finalEdges?: ReactFlowEdge[]
    ): Promise<WorkflowGraphWriteResult | null> => {
      // We discard flush()'s return value because it's the same promise that
      // doSave stored into inFlightRef.current — awaiting that on the next line
      // covers both this just-kicked-off save and any save already in flight.
      const _ = debouncedSave.flush();
      await inFlightRef.current;
      if (finalNodes === undefined || finalEdges === undefined) {
        return null;
      }
      return new Promise<WorkflowGraphWriteResult>((resolve, reject) => {
        const bytes = lbWorkflowToBase64(
          reactFlowToLBWorkflow(finalNodes, finalEdges)
        );
        const snapshot = {
          workflowId,
          nodes: finalNodes,
          edges: finalEdges,
          onPersist,
          bytes,
          completion: { resolve, reject },
        };
        pendingRef.current = snapshot;
        unsavedRef.current = snapshot;
        void doSave();
      });
    },
    [debouncedSave, doSave, onPersist, workflowId]
  );

  const adoptAuthoritativeSnapshot = useCallback(
    (
      authoritativeNodes: ReactFlowNode[],
      authoritativeEdges: ReactFlowEdge[]
    ) => {
      if (inFlightRef.current !== null || pendingRef.current !== null) {
        throw new Error(
          'Cannot adopt an authoritative workflow snapshot while a save is pending'
        );
      }
      debouncedSave.cancel();
      unsavedRef.current = null;
      lastBytesRef.current = lbWorkflowToBase64(
        reactFlowToLBWorkflow(authoritativeNodes, authoritativeEdges)
      );
      setStatus('idle');
    },
    [debouncedSave]
  );

  // Flush any pending save on unmount so navigating away doesn't drop edits.
  // Fire-and-forget — the underlying network request continues post-unmount.
  useEffect(() => {
    return () => {
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  // Last-ditch save when the page is about to go away. Axios's regular request
  // gets killed by the browser during unload; instead we ask the caller to
  // fire a keepalive-backed request that survives the document's destruction.
  //
  // We intentionally avoid `beforeunload` / `unload` — Chrome 117+ enforces a
  // Permissions Policy that blocks those events and marks the page as
  // bfcache-ineligible. `pagehide` (fires on close, navigation, and page
  // discard) combined with `visibilitychange` (hidden) covers the same cases.
  useEffect(() => {
    const persistOnUnload = () => {
      const unsaved = unsavedRef.current;
      if (unsaved === null) {
        return;
      }
      // Fire-and-forget: the browser handles delivery after the page is gone.
      // Swallow rejections so a failed last-ditch save doesn't surface as an
      // unhandled promise rejection during page hide — there's no UI to react.
      const _ = unsaved
        .onPersist(unsaved.nodes, unsaved.edges, { keepalive: true })
        .catch((err: unknown) => {
          console.warn('Keepalive persist failed during page hide', err);
        });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        persistOnUnload();
      }
    };
    window.addEventListener('pagehide', persistOnUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', persistOnUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return { status, lastSavedAt, flushSave, adoptAuthoritativeSnapshot };
}
